import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    BadgeCheck,
    BookOpen,
    BriefcaseBusiness,
    Check,
    CheckCircle2,
    CreditCard,
    Eye,
    FileText,
    GraduationCap,
    Heart,
    Image as ImageIcon,
    Mail,
    MapPin,
    Moon,
    Paperclip,
    Pencil,
    Phone,
    RotateCcw,
    ShieldCheck,
    Store,
    Clock3,
    RotateCw,
    ShieldAlert,
    UserCog,
    UserRound,
    Users,
    X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SafeImage from '../../components/SafeImage';
import PrivateDoc from '../../components/PrivateDoc';
import ContactVerifyRow from '../../components/ContactVerifyRow';
import Select from '../../components/Select';
import RejectVerificationModal from '../../components/RejectVerificationModal';
import API from '../../utils/api';
import { aadharStatusMeta, initialFor, mediaUrl } from './profileShared';
import { RULES, checkFiles, limitNote, uploadFailMessage } from '../../utils/uploadRules';
import './ProfileDetail.css';

const ProfileDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [aadharActionLoading, setAadharActionLoading] = useState(false);
    const [uploadingAadharDoc, setUploadingAadharDoc] = useState(false);
    const [revealedAadhar, setRevealedAadhar] = useState('');
    const [revealingAadhar, setRevealingAadhar] = useState(false);

    // The full number is encrypted on the server; revealing it is an explicit, logged action.
    const handleRevealAadhar = async () => {
        setRevealingAadhar(true);
        try {
            const res = await API.get(`/admin/profiles/${id}/aadhar-number`);
            setRevealedAadhar(res.data.aadharNumber || 'Not available');
        } catch (error) {
            toast.error('Could not load the Aadhaar number');
        } finally {
            setRevealingAadhar(false);
        }
    };
    const [linkedVendor, setLinkedVendor] = useState(null);
    const [verifierAdmins, setVerifierAdmins] = useState([]);
    const [verifierLoadFailed, setVerifierLoadFailed] = useState(false);

    const fetchProfile = useCallback(async () => {
        try {
            const res = await API.get('/admin/profiles');
            const found = (res.data.profiles || []).find(item => item._id === id);
            if (!found) {
                setNotFound(true);
                return;
            }
            setProfile(found);
            if (found.user?.mobile) {
                API.get('/admin/vendors')
                    .then(vendorsRes => {
                        const match = (vendorsRes.data.vendors || []).find(vendor => vendor.mobile === found.user.mobile);
                        setLinkedVendor(match || null);
                    })
                    .catch(() => {});
            }
        } catch (err) {
            toast.error('Failed to load profile');
        }
    }, [id]);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    useEffect(() => {
        API.get('/admin/admins')
            .then(res => {
                setVerifierAdmins((res.data.admins || []).filter(admin => admin.canVerifyProfiles));
                setVerifierLoadFailed(false);
            })
            .catch(() => setVerifierLoadFailed(true));
    }, []);

    const handleAssignVerifier = async adminId => {
        if (!profile?.user?._id) return;
        try {
            const res = await API.put(`/admin/users/${profile.user._id}/assign-verifier`, { adminId: adminId || null });
            toast.success(res.data.message);
            setProfile(current => ({ ...current, user: { ...current.user, assignedVerifier: res.data.user.assignedVerifier } }));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update verifier');
        }
    };

    const handleVerify = async () => {
        try {
            await API.put(`/admin/profiles/${id}/verify`);
            toast.success('Profile verified!');
            setProfile(current => ({ ...current, isVerified: true }));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to verify');
        }
    };

    const handleApproveAadhar = async () => {
        setAadharActionLoading(true);
        try {
            const res = await API.put(`/admin/profiles/${id}/aadhar/approve`);
            toast.success('Aadhaar approved');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error('Failed to approve Aadhaar');
        } finally {
            setAadharActionLoading(false);
        }
    };

    const handleRejectAadhar = async () => {
        setAadharActionLoading(true);
        try {
            const res = await API.put(`/admin/profiles/${id}/aadhar/reject`, { reason: rejectReason || undefined });
            toast.success('Aadhaar rejected');
            setRejectReason('');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error('Failed to reject Aadhaar');
        } finally {
            setAadharActionLoading(false);
        }
    };

    const handleRevokeAadhar = async () => {
        if (!rejectReason.trim()) {
            toast.error('A reason is required to revoke Aadhaar approval');
            return;
        }
        setAadharActionLoading(true);
        try {
            const res = await API.put(`/admin/profiles/${id}/aadhar/revoke`, { reason: rejectReason });
            toast.success('Aadhaar approval revoked');
            setRejectReason('');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to revoke Aadhaar');
        } finally {
            setAadharActionLoading(false);
        }
    };

    const handleUploadAadharDocAdmin = async files => {
        if (!Array.from(files || []).length) return;
        const fileList = checkFiles(files, RULES.document, { have: profile.aadharDocuments?.length || 0, maxFiles: 2 });
        if (!fileList.length) return;
        setUploadingAadharDoc(true);
        const formData = new FormData();
        fileList.forEach(file => formData.append('documents', file));
        try {
            const res = await API.post(`/admin/profiles/${id}/aadhar-doc`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Aadhaar document(s) uploaded');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error(uploadFailMessage(err, fileList, RULES.document));
        } finally {
            setUploadingAadharDoc(false);
        }
    };

    const handleDeleteAadharDocAdmin = async docUrl => {
        try {
            const filename = docUrl.split('/').pop();
            const res = await API.delete(`/admin/profiles/${id}/aadhar-doc/${filename}`);
            toast.success('Document removed');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error('Failed to remove document');
        }
    };

    const shell = children => (
        <div className="profile-detail-layout">
            <Sidebar />
            <div className="admin-content profile-detail-content">
                <Navbar title={<span className="profile-detail-nav-title"><UserRound size={18} /> Profile detail</span>} />
                {children}
            </div>
        </div>
    );

    if (notFound) {
        return shell(
            <main className="profile-detail-page">
                <button type="button" className="profile-detail-back" onClick={() => navigate('/profiles')}><ArrowLeft size={16} /> Back to profiles</button>
                <section className="profile-detail-not-found">
                    <span><UserRound size={30} /></span>
                    <h1>Profile not found</h1>
                    <p>This profile may have been deleted or is no longer available.</p>
                </section>
            </main>
        );
    }

    if (!profile) return shell(null);

    const displayName = profile.name || profile.user?.name || 'Unnamed member';
    const aadhar = aadharStatusMeta(profile.aadharStatus);
    // The member dashboard displays the profile's primary photo first. Keep
    // the admin detail view consistent; the first gallery item can be stale
    // while `profile.photo` still points at the current upload.
    const photos = [profile.photo, ...(profile.photos || [])]
        .filter(Boolean)
        .filter((photo, index, allPhotos) => allPhotos.indexOf(photo) === index);
    const assignedVerifierId = typeof profile.user?.assignedVerifier === 'string'
        ? profile.user.assignedVerifier
        : profile.user?.assignedVerifier?._id || '';
    const personalSummary = [profile.age ? `${profile.age} years` : null, profile.religion, profile.caste].filter(Boolean).join(' · ');

    return shell(
        <main className="profile-detail-page">
            <div className="profile-detail-topbar">
                <button type="button" className="profile-detail-back" onClick={() => navigate('/profiles')}>
                    <ArrowLeft size={16} /> Back to profiles
                </button>
                <span className="profile-detail-id">Member ID: {profile.memberId || profile.user?.memberId || 'Pending assignment'}</span>
            </div>

            <section className="profile-detail-hero">
                <div className="profile-detail-hero__glow" aria-hidden="true"><Heart size={88} strokeWidth={0.8} /></div>
                <div className={`profile-detail-avatar ${photos[0] ? '' : 'is-placeholder'}`} aria-label={photos[0] ? `${displayName} profile photo` : 'Profile image placeholder'}>
                    {photos[0]
                        ? <div className="profile-detail-avatar__photo"><SafeImage src={mediaUrl(photos[0])} alt={displayName} className="profile-detail-avatar__image" /></div>
                        : <UserRound size={58} strokeWidth={1.7} />}
                    {profile.isVerified && <i><Check size={14} /></i>}
                </div>

                <div className="profile-detail-identity">
                    <div className="profile-detail-name-row">
                        <h1>{displayName}</h1>
                        {profile.isVerified ? (
                            <span className="profile-detail-verified"><BadgeCheck size={15} /> Verified</span>
                        ) : profile.verificationStatus === 'rejected' ? (
                            <span className="profile-detail-unverified" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
                                <ShieldAlert size={14} /> Verification rejected
                            </span>
                        ) : profile.verificationStatus === 'resubmitted' ? (
                            <span className="profile-detail-unverified" style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}>
                                <RotateCw size={14} /> Verification resubmitted
                            </span>
                        ) : (
                            <span className="profile-detail-unverified"><Clock3 size={14} /> Verification pending</span>
                        )}
                    </div>
                    <p className="profile-detail-summary">{personalSummary || 'Matrimony member profile'}</p>
                    {profile.profileFor && profile.profileFor !== 'Myself' && (
                        <span className="profile-detail-for"><UserRound size={12} /> Registered by {profile.user?.name || 'account holder'} for {profile.profileFor}</span>
                    )}
                    {profile.verificationStatus === 'rejected' && profile.verificationRejectReason && (
                        <div style={{ margin: '8px 0', padding: '8px 12px', background: '#fef2f2', border: '1px dashed #fca5a5', borderRadius: '8px', fontSize: '0.82rem', color: '#991b1b' }}>
                            <strong>Rejection reason sent to member:</strong> "{profile.verificationRejectReason}"
                        </div>
                    )}
                    <div className="profile-detail-contact-line">
                        {profile.user?.mobile && <span><Phone size={14} /> {profile.user.mobile}</span>}
                        {profile.user?.email && <span><Mail size={14} /> {profile.user.email}</span>}
                    </div>
                </div>

                <div className="profile-detail-actions">
                    <button type="button" className="profile-detail-action profile-detail-action--edit" onClick={() => navigate(`/profiles/${id}/edit`)}>
                        <Pencil size={16} /> Edit profile
                    </button>
                    {!profile.isVerified && (
                        <>
                            <button
                                type="button"
                                className="profile-detail-action profile-detail-action--verify"
                                title="Profile verification is independent from Aadhaar verification"
                                onClick={handleVerify}
                            >
                                <CheckCircle2 size={16} /> Verify profile
                            </button>
                            <button
                                type="button"
                                className="profile-detail-action"
                                style={{
                                    background: '#fee2e2',
                                    color: '#991b1b',
                                    border: '1px solid #fecaca',
                                    padding: '9px 18px',
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    fontSize: '0.86rem',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '7px'
                                }}
                                title="Reject verification and send feedback message to this member"
                                onClick={() => setRejectModalOpen(true)}
                            >
                                <ShieldAlert size={16} /> {profile.verificationStatus === 'rejected' ? 'Update rejection' : 'Reject verification'}
                            </button>
                        </>
                    )}
                </div>

                <div className="profile-detail-hero__footer">
                    <div className="profile-detail-verifier">
                        <span><UserCog size={15} /> Assigned verifier</span>
                        {verifierAdmins.length > 0 ? (
                            <Select
                                className="profile-detail-verifier-select"
                                value={assignedVerifierId}
                                onChange={handleAssignVerifier}
                                options={verifierAdmins.map(admin => ({ value: admin._id, label: admin.name }))}
                                placeholder="Unassigned"
                                ariaLabel="Assigned profile verifier"
                            />
                        ) : (
                            <button type="button" className="profile-detail-verifier-setup" onClick={() => navigate('/admin-access')}>
                                {verifierLoadFailed ? 'Could not load verifiers' : 'Set up a profile verifier'}
                            </button>
                        )}
                    </div>
                    <div className="profile-detail-id-status">
                        <span>Identity status</span>
                        <strong style={{ '--status-color': aadhar.color, '--status-bg': aadhar.bg }}><CreditCard size={14} /> Aadhaar: {aadhar.label}</strong>
                    </div>
                </div>
            </section>

            {!profile.isVerified && profile.aadharStatus !== 'approved' && (
                <div className="profile-detail-notice">
                    <ShieldCheck size={18} />
                    <div><strong>Profile verification is locked</strong><span>Review and approve the Aadhaar documents below to enable verification.</span></div>
                </div>
            )}

            {linkedVendor && (
                <section className="profile-detail-vendor">
                    <span className="profile-detail-vendor__icon"><Store size={20} /></span>
                    <div><small>Linked service provider account</small><strong>{linkedVendor.businessName}</strong></div>
                    <div className="profile-detail-vendor__actions">
                        <button type="button" onClick={() => navigate(`/service-providers/${linkedVendor._id}`)}><Eye size={14} /> View</button>
                        <button type="button" onClick={() => navigate(`/service-providers/${linkedVendor._id}/edit`)}><Pencil size={14} /> Edit</button>
                    </div>
                </section>
            )}

            {photos.length > 0 && (
                <DetailSection className="profile-detail-section--wide" icon={ImageIcon} title="Profile gallery" subtitle={`${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`}>
                    <div className="profile-detail-gallery">
                        {photos.map((photo, index) => (
                            <a href={mediaUrl(photo)} target="_blank" rel="noopener noreferrer" key={`${photo}-${index}`}>
                                <SafeImage src={mediaUrl(photo)} alt={`${displayName} profile ${index + 1}`} />
                            </a>
                        ))}
                    </div>
                </DetailSection>
            )}

            <div className="profile-detail-grid">
                <DetailSection icon={Phone} title="Contact information">
                    <ContactVerifyRow variant="detail" label="Mobile" type="mobile" userId={profile.user?._id} value={profile.user?.mobile} verified={profile.user?.isMobileVerified !== false} onChanged={fetchProfile} />
                    <ContactVerifyRow variant="detail" label="Email" type="email" userId={profile.user?._id} value={profile.user?.email} verified={Boolean(profile.user?.isEmailVerified)} onChanged={fetchProfile} />
                    {profile.createdByName && <DetailRow label="Registered by" value={`${profile.createdByName} (${profile.profileFor})`} />}
                </DetailSection>

                <DetailSection icon={UserRound} title="Personal details">
                    <DetailRow label="Gender" value={profile.gender} />
                    <DetailRow label="Date of birth" value={profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString('en-IN') : null} />
                    <DetailRow label="Height / Weight" value={[profile.height, profile.weight].filter(Boolean).join(' / ')} />
                    <DetailRow label="Complexion" value={profile.complexion} />
                    <DetailRow label="Marital status" value={profile.maritalStatus} />
                    <DetailRow label="Family type" value={profile.familyType} />
                    <DetailRow label="Mother tongue" value={profile.motherTongue} />
                    <DetailRow label="Known languages" value={profile.knownLanguages?.join(', ')} />
                </DetailSection>

                <DetailSection icon={BookOpen} title="Religion & community">
                    <DetailRow label="Religion" value={profile.religion} />
                    <DetailRow label="Caste / Sub caste" value={[profile.caste, profile.subCaste].filter(Boolean).join(' / ')} />
                    <DetailRow label="Gothra" value={profile.gothra} />
                </DetailSection>

                <DetailSection icon={Moon} title="Horoscope details">
                    <DetailRow label="Rasi (Zodiac)" value={profile.rasi} />
                    <DetailRow label="Nakshatra (Star)" value={profile.nakshatra} />
                    <DetailRow label="Dosham" value={profile.dosham} />
                    {profile.horoscopeDocuments?.length > 0 && (
                        <DocumentGrid documents={profile.horoscopeDocuments} label="Horoscope document" />
                    )}
                </DetailSection>

                <DetailSection icon={GraduationCap} title="Education & career">
                    <DetailRow label="Education" value={profile.education} />
                    <DetailRow label="Employment" value={profile.employed} />
                    <DetailRow label="Occupation" value={profile.occupation} />
                    {profile.occupationRemark && <DetailRow label="Occupation notes" value={profile.occupationRemark} />}
                    <DetailRow label="Annual income" value={profile.annualIncome} />
                </DetailSection>

                <DetailSection icon={MapPin} title="Location">
                    <DetailRow label="Home location" value={[profile.city, profile.district, profile.state].filter(Boolean).join(' / ')} />
                    <DetailRow label="Country" value={profile.country} />
                    {profile.pincode && <DetailRow label="PIN / postal code" value={profile.pincode} />}
                    {(profile.workingCity || profile.workingDistrict || profile.workingState) && (
                        <DetailRow label="Working location" value={[profile.workingCity, profile.workingDistrict, profile.workingState, profile.workingCountry].filter(Boolean).join(' / ')} />
                    )}
                    {profile.workingPincode && <DetailRow label="Working PIN / postal code" value={profile.workingPincode} />}
                </DetailSection>

                {(profile.parentsStatus || profile.fatherName || profile.fatherOccupation || profile.fatherSector || profile.motherName || profile.motherOccupation || profile.motherSector || profile.siblings) && (
                    <DetailSection icon={Users} title="Family details">
                        <DetailRow label="Parents' status" value={profile.parentsStatus} />
                        <DetailRow label="Father's name" value={profile.fatherName} />
                        <DetailRow label="Father's occupation" value={profile.fatherOccupation} />
                        <DetailRow label="Father's sector" value={profile.fatherSector} />
                        <DetailRow label="Mother's name" value={profile.motherName} />
                        <DetailRow label="Mother's occupation" value={profile.motherOccupation} />
                        <DetailRow label="Mother's sector" value={profile.motherSector} />
                        <DetailRow label="Siblings" value={profile.siblings} />
                    </DetailSection>
                )}

                {profile.about && (
                    <DetailSection icon={FileText} title="About this member">
                        <p className="profile-detail-about">{profile.about}</p>
                    </DetailSection>
                )}

                {profile.customFields?.length > 0 && (
                    <DetailSection className="profile-detail-section--wide profile-detail-custom" icon={BriefcaseBusiness} title="Custom profile entries">
                        <p><strong>{profile.customFields.join(', ')}</strong> were entered using “Other”. Review these values under Profile Options → Pending Suggestions.</p>
                    </DetailSection>
                )}

                <section className="profile-detail-aadhar">
                    <div className="profile-detail-aadhar__header">
                        <span className="profile-detail-aadhar__icon"><ShieldCheck size={23} /></span>
                        <div><small>Identity review</small><h2>Aadhaar verification</h2><p>Review submitted identity proof before marking this matrimony profile as verified.</p></div>
                        <strong style={{ '--status-color': aadhar.color, '--status-bg': aadhar.bg }}>{aadhar.label}</strong>
                    </div>

                    <div className="profile-detail-aadhar__body">
                        <div className="profile-detail-aadhar__number">
                            <span>Aadhaar number</span>
                            <strong>{revealedAadhar || (profile.aadharLast4 ? `XXXX XXXX ${profile.aadharLast4}` : 'Not submitted')}</strong>
                            {profile.aadharLast4 && !revealedAadhar && (
                                <button type="button" onClick={handleRevealAadhar} disabled={revealingAadhar}>{revealingAadhar ? 'Loading…' : 'Reveal full number'}</button>
                            )}
                        </div>

                        {profile.aadharDocuments?.length > 0 && (
                            <div className="profile-detail-documents">
                                <span className="profile-detail-documents__label">Submitted documents</span>
                                <div className="profile-detail-document-grid">
                                    {profile.aadharDocuments.map((document, index) => (
                                        <div className="profile-detail-document" key={`${document}-${index}`}>
                                            <PrivateDoc url={document} alt={`Aadhaar document ${index + 1}`} pdfClassName="profile-detail-pdf" pdfLabel="PDF document" />
                                            {profile.aadharStatus !== 'approved' && (
                                                <button type="button" onClick={() => handleDeleteAadharDocAdmin(document)} aria-label="Remove Aadhaar document"><X size={13} /></button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {profile.aadharStatus !== 'approved' && (profile.aadharDocuments?.length || 0) < 2 && (
                            <label className={`profile-detail-upload ${uploadingAadharDoc ? 'is-loading' : ''}`}>
                                <Paperclip size={16} /> {uploadingAadharDoc ? 'Uploading…' : 'Attach Aadhaar photo or PDF'}
                                <small className="upload-limit">{limitNote(RULES.document, { maxFiles: 2 })}</small>
                                <input
                                    type="file"
                                    accept="image/*,.pdf,application/pdf"
                                    multiple
                                    disabled={uploadingAadharDoc}
                                    onChange={event => { handleUploadAadharDocAdmin(event.target.files); event.target.value = ''; }}
                                />
                            </label>
                        )}

                        {(profile.aadharStatus === 'pending' || profile.aadharStatus === 'rejected') && (
                            <div className="profile-detail-review-actions">
                                <label><span>Review note</span><input placeholder="Optional rejection reason shown to the member" value={rejectReason} onChange={event => setRejectReason(event.target.value)} /></label>
                                <div>
                                    <button type="button" className="approve" disabled={aadharActionLoading} onClick={handleApproveAadhar}><Check size={15} /> Approve Aadhaar</button>
                                    <button type="button" className="reject" disabled={aadharActionLoading} onClick={handleRejectAadhar}><X size={15} /> Reject</button>
                                </div>
                            </div>
                        )}

                        {profile.aadharStatus === 'approved' && (
                            <div className="profile-detail-review-actions">
                                <label><span>Reason for revoking approval</span><input placeholder="Required — this will be shown to the member" value={rejectReason} onChange={event => setRejectReason(event.target.value)} /></label>
                                <div><button type="button" className="revoke" disabled={aadharActionLoading} onClick={handleRevokeAadhar}><RotateCcw size={15} /> Revoke approval</button></div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <RejectVerificationModal
                open={rejectModalOpen}
                target={profile}
                isProfile={true}
                onClose={() => setRejectModalOpen(false)}
                onSuccess={(data) => {
                    if (data?.profile) {
                        setProfile(prev => ({ ...prev, ...data.profile }));
                    } else {
                        fetchProfile();
                    }
                }}
            />
        </main>
    );
};

const DetailSection = ({ icon: Icon, title, subtitle, className = '', children }) => (
    <section className={`profile-detail-section ${className}`}>
        <header><span><Icon size={17} /></span><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div></header>
        <div className="profile-detail-section__body">{children}</div>
    </section>
);

const DetailRow = ({ label, value }) => (
    <div className="profile-detail-row"><span>{label}</span><strong>{value || '—'}</strong></div>
);

const DocumentGrid = ({ documents, label }) => (
    <div className="profile-detail-mini-docs">
        {documents.map((document, index) => (
            <PrivateDoc key={`${document}-${index}`} url={document} alt={`${label} ${index + 1}`} />
        ))}
    </div>
);

export default ProfileDetail;
